import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import { fetchBitcoinPrice, type BitcoinPriceData } from '@/data-access/blocks';

interface BitcoinPriceCardProps {
  className?: string;
}

const defaultPriceData: BitcoinPriceData = {
  price: 43756.82,
  change24h: 2.34
};

export function BitcoinPriceCard({ 
  className 
}: BitcoinPriceCardProps) {
  const { 
    data: priceData, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['bitcoin-price'],
    queryFn: fetchBitcoinPrice,
    refetchInterval: 60000, // Refetch every 60 seconds
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  // Use real data if available, otherwise fall back to default
  const displayData = priceData || defaultPriceData;
  const isPositive = displayData.change24h >= 0;
  
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900/20">
            <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
              <img src="/assets/btc_logo.png" alt="DMG Blockchain" />
            </div>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-foreground">Bitcoin Price</h3>
              {error && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-xs text-muted-foreground">Cached</span>
                </div>
              )}
              {!isLoading && !error && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-muted-foreground">Live</span>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">USD Market Value</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="flex items-end justify-between">
            <div className="space-y-1">
              <Skeleton className="h-9 w-40" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-6 w-20" />
          </div>
        ) : (
          <div className="flex items-end justify-between">
            <div className="space-y-1">
              <p className="text-3xl font-bold text-foreground">
                ${displayData.price.toLocaleString('en-US', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </p>
              <p className="text-sm text-muted-foreground">24h Market Change</p>
            </div>
            <Badge 
              variant={isPositive ? "default" : "destructive"}
              className={`flex items-center space-x-1 ${
                isPositive 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' 
                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              }`}
            >
              {isPositive ? (
                <TrendingUp className="w-3 h-3" />
              ) : (
                <TrendingDown className="w-3 h-3" />
              )}
              <span>{isPositive ? '+' : ''}{displayData.change24h.toFixed(2)}%</span>
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}